import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by navigating to media that was loaded with the page.
   * The gallery is replaced only when a combined-listing selection points to media that
   * does not exist in the current product gallery.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const selectedFromOption = event.detail?.mediaId ? this.#selectMedia(event.detail.mediaId) : false;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const mediaId = detail.resource?.featured_media?.id;
        if (!detail.newProduct && !selectedFromOption && mediaId != null && this.#selectMedia(mediaId)) return;

        if (detail.newProduct) this.#replaceGallery(detail.html);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Selects already-rendered media in the mobile slideshow and desktop grid.
   *
   * @param {number|string} mediaId - The Shopify media ID.
   * @returns {boolean} Whether the media exists in this gallery.
   */
  #selectMedia(mediaId) {
    const id = String(mediaId);
    const slides = this.slideshow?.refs.slides ?? [];
    const slide = slides.find((item) => item.getAttribute('slide-id') === id);
    const gridMedia = Array.from(this.querySelectorAll('.media-gallery__grid > [data-variant-media]'));
    const gridItem = gridMedia.find(
      (item) => item.querySelector('.product-media')?.getAttribute('data-media-id') === id
    );

    if (!slide && !gridItem) return false;

    if (slide) {
      for (const item of slides) {
        const selected = item === slide;
        item.hidden = !selected;
        item.removeAttribute('reveal');
        item.setAttribute('aria-hidden', String(!selected));
      }

      if (this.slideshow?.refs.scroller) this.slideshow.refs.scroller.scrollLeft = 0;
    }

    if (gridItem) {
      for (const item of gridMedia) item.hidden = item !== gridItem;

      requestAnimationFrame(() => {
        if (gridItem.offsetParent !== null) {
          gridItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });
    }

    return true;
  }

  /**
   * Replaces the gallery when navigating to a different product in a combined listing.
   *
   * @param {Document} html - Parsed section HTML for the new product.
   */
  #replaceGallery(html) {
    const newMediaGallery = html?.querySelector('media-gallery');
    if (newMediaGallery) this.replaceWith(newMediaGallery);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
